/**
 * TopNavMobile.tsx -- Mobile navigation chrome (FR-006 — T306).
 *
 * Rendered inside the `.topnav__mobile` display-switching wrapper in TopNav.
 * Contains: wordmark + hamburger + search trigger + notifications bell + avatar dropdown.
 * Hamburger opens a slide-in drawer with the four primary menu items, a divider,
 * a role-tag chip, and sign-out. Focus trap on open; Escape closes returning
 * focus to the trigger.
 *
 * Consumes only CSS custom-property references (var(--*)) for styling.
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link, useHashLocation } from "../../lib/router";
import { useCurrentUserId, useFeatureFlag } from "../../api/hooks";
import { useCommandPalette } from "../providers/CommandPaletteProvider";
import { useToastConfirm } from "../cross-cutting/useToastConfirm";
import { usePrefetchRoute } from "../../lib/usePrefetchRoute";
import { getPrefetchFactory } from "../../lib/routePrefetch";
import { Icon } from "../primitives";
import { NotificationBellTrigger } from "./NotificationBellTrigger";
import SubscriptionScope from "./SubscriptionScope";
import type { CSSProperties } from "react";

/** Capicua wordmark prefix — not a translatable string. */
const WORDMARK_PREFIX = "Capic";

/** DOM selector for focusable elements in the drawer focus trap. */
const FOCUSABLE_ELEMENTS = `a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])`;

// ---------------------------------------------------------------------------
// Menu items (canonical 4-item set, same as TopNav desktop)
// ---------------------------------------------------------------------------

interface MobileMenuItem {
  id: string;
  label: string;
  href: string;
  match: (route: string) => boolean;
}

const MENU_ITEMS: MobileMenuItem[] = [
  {
    id: "command-center",
    label: "Command Center",
    href: "/today",
    match: (r) =>
      r === "/" ||
      r.startsWith("/today"),
  },
  {
    id: "workspaces",
    label: "Workspaces",
    href: "/workspaces",
    match: (r) => r.startsWith("/workspaces") || r.startsWith("/workspace/"),
  },
  {
    id: "activity",
    label: "Activity",
    href: "/activity",
    match: (r) => r.startsWith("/activity"),
  },
  {
    id: "library",
    label: "Library",
    href: "/library",
    match: (r) =>
      r.startsWith("/library") ||
      r.startsWith("/playbooks") ||
      r.startsWith("/docs/"),
  },
];

// ---------------------------------------------------------------------------
// Styles -- CSS custom-property references only
// ---------------------------------------------------------------------------

const mobileBarStyle: CSSProperties = {
  alignItems: "center",
  justifyContent: "space-between",
  height: "56px",
  padding: "0 var(--space-md, 12px)",
  background: "var(--bg1, #ffffff)",
  borderBottom: "1px solid var(--bg2, #eeece7)",
  width: "100%",
};

const iconButtonStyle: CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "var(--space-xs, 6px)",
  minWidth: "44px",
  minHeight: "44px",
};

const drawerNavLinkStyle: CSSProperties = {
  display: "block",
  padding: "var(--space-lg, 16px) var(--space-xl, 24px)",
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-body-lg, 18px)",
  color: "var(--fg1, #212121)",
  textDecoration: "none",
};

const drawerNavLinkActiveStyle: CSSProperties = {
  ...drawerNavLinkStyle,
  background: "var(--bg2, #eeece7)",
  borderLeft: "3px solid var(--color-primary, #1e293b)",
};

const roleChipStyle: CSSProperties = {
  fontFamily: "var(--font-mono, monospace)",
  fontSize: "var(--fs-micro, 12px)",
  textTransform: "uppercase" as const,
  border: "1px solid var(--fg4, #93939f)",
  borderRadius: "var(--radius-pill, 32px)",
  padding: "var(--space-xxs, 2px) var(--space-sm, 8px)",
  letterSpacing: "0.05em",
  color: "var(--fg3, #75758a)",
};

const signOutButtonStyle: CSSProperties = {
  display: "block",
  width: "100%",
  padding: "var(--space-lg, 16px) var(--space-xl, 24px)",
  fontFamily: "var(--font-ui)",
  fontSize: "var(--fs-body, 16px)",
  color: "var(--color-error, #dc2626)",
  textAlign: "left" as const,
  background: "none",
  border: "none",
  cursor: "pointer",
};

// ---------------------------------------------------------------------------
// PrefetchDrawerLink — wraps a Link with usePrefetchRoute handlers
// ---------------------------------------------------------------------------

function PrefetchDrawerLink({
  item,
  isActive,
  activeStyle,
  inactiveStyle,
}: {
  item: MobileMenuItem;
  isActive: boolean;
  activeStyle: CSSProperties;
  inactiveStyle: CSSProperties;
}) {
  const factory = getPrefetchFactory(item.href);
  const prefetch = usePrefetchRoute(factory);

  return (
    <Link
      href={item.href}
      style={isActive ? activeStyle : inactiveStyle}
      aria-current={isActive ? "page" : undefined}
      onPointerEnter={prefetch.onPointerEnter}
      onPointerLeave={prefetch.onPointerLeave}
      onFocus={prefetch.onFocus}
      onBlur={prefetch.onBlur}
    >
      {item.label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// TopNavMobile component
// ---------------------------------------------------------------------------

export function TopNavMobile() {
  const [path] = useHashLocation();
  const userId = useCurrentUserId();
  const notifEnabled = useFeatureFlag("notifications_inbox_v1");
  const { open: openPalette } = useCommandPalette();
  const showWithUndo = useToastConfirm();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Sign-out handler
  const handleSignOut = useCallback(() => {
    setDrawerOpen(false);
    showWithUndo({
      onConfirm: () => {
        // Sign-out mutation wired by auth layer
      },
    });
  }, [showWithUndo]);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    // Return focus to hamburger trigger
    hamburgerRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!drawerOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeDrawer();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [drawerOpen, closeDrawer]);

  // Focus trap: keep focus inside drawer when open
  useEffect(() => {
    if (!drawerOpen || !drawerRef.current) return;

    const drawer = drawerRef.current;
    // Focus the first focusable element
    const firstFocusable = drawer.querySelector<HTMLElement>(FOCUSABLE_ELEMENTS);
    firstFocusable?.focus();

    function handleTab(e: KeyboardEvent) {
      if (e.key !== "Tab") return;

      const focusables = drawer.querySelectorAll<HTMLElement>(focusableSelector);
      if (focusables.length === 0) return;

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleTab);
    return () => {
      document.removeEventListener("keydown", handleTab);
    };
  }, [drawerOpen]);

  // Close drawer on navigation
  useEffect(() => {
    if (drawerOpen) {
      setDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return (
    <>
      {/* Mobile navigation bar */}
      <div data-testid="topnav-mobile-bar" style={mobileBarStyle}>
        {/* Left: Hamburger */}
        <button
          ref={hamburgerRef}
          type="button"
          aria-label="Open navigation menu"
          aria-expanded={drawerOpen}
          onClick={openDrawer}
          style={iconButtonStyle}
          data-testid="topnav-mobile-hamburger"
        >
          <Icon name="menu" size={24} />
        </button>

        {/* Centre: Wordmark */}
        <span aria-label="Capicua home">
          {WORDMARK_PREFIX}
          <span style={{ position: "relative" }}>{"u\u0301"}</span>
          {"a"}
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 5,
              height: 5,
              background: "var(--color-coral)",
              marginLeft: 1,
              transform: "translateY(-12px)",
            }}
          />
        </span>

        {/* Right: utility cluster */}
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-xs, 6px)" }}>
          {/* Search trigger */}
          <button
            type="button"
            aria-label="Search"
            onClick={openPalette}
            style={iconButtonStyle}
          >
            <Icon name="search" size={20} />
          </button>

          {/* Notifications bell */}
          {notifEnabled === true && userId != null && (
            <SubscriptionScope key={userId} active>
              <NotificationBellTrigger />
            </SubscriptionScope>
          )}

          {/* Avatar */}
          <button
            type="button"
            aria-label="User menu"
            style={{
              ...iconButtonStyle,
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "var(--fg3, #75758a)",
              color: "var(--fg-on-dark, #ffffff)",
              fontSize: "var(--fs-caption, 14px)",
              fontWeight: 600,
              minWidth: "32px",
              minHeight: "32px",
            }}
          >
            <Icon name="user" size={16} />
          </button>
        </div>
      </div>

      {/* Slide-in drawer */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="topnav-mobile__drawer-backdrop"
            onClick={closeDrawer}
            aria-hidden="true"
          />

          {/* Drawer panel */}
          <div
            ref={drawerRef}
            className="topnav-mobile__drawer"
            role="dialog"
            aria-label="Navigation menu"
            aria-modal="true"
            data-testid="topnav-mobile-drawer"
          >
            {/* Close button */}
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "var(--space-sm, 8px)" }}>
              <button
                type="button"
                aria-label="Close navigation menu"
                onClick={closeDrawer}
                style={iconButtonStyle}
              >
                <Icon name="x" size={24} />
              </button>
            </div>

            {/* Navigation links */}
            <nav aria-label="Primary mobile" style={{ flex: 1 }}>
              {MENU_ITEMS.map((item) => {
                const isActive = item.match(path);
                return (
                  <PrefetchDrawerLink
                    key={item.id}
                    item={item}
                    isActive={isActive}
                    activeStyle={drawerNavLinkActiveStyle}
                    inactiveStyle={drawerNavLinkStyle}
                  />
                );
              })}
            </nav>

            {/* Divider */}
            <hr
              role="separator"
              style={{
                border: "none",
                borderTop: "1px solid var(--color-border, #e5e7eb)",
                margin: "var(--space-sm, 8px) var(--space-xl, 24px)",
              }}
            />

            {/* Role-tag chip */}
            <div style={{ padding: "var(--space-sm, 8px) var(--space-xl, 24px)" }}>
              <span style={roleChipStyle}>Operator</span>
            </div>

            {/* Sign out */}
            <button
              type="button"
              onClick={handleSignOut}
              style={signOutButtonStyle}
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default TopNavMobile;
